import React, { useContext, useEffect, useRef, useState } from 'react'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import { getMyOrganizations, updateOrgDetails } from '../../utilities/api'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { Spinner } from '../LoadingContainer/Spinner'
import { PageLoadingContainer } from '../PageLoadingContainer'

const getTrackingStateFromOrg = (org = {}) => ({
  id: org.id,
  tracking_enabled: org.tracking_enabled === true,
  google_tag_manager_id: org.google_tag_manager_id || '',
  google_ads_id: org.google_ads_id || '',
  google_ads_conversion_label: org.google_ads_conversion_label || '',
  tiktok_pixel_id: org.tiktok_pixel_id || '',
  linkedin_partner_id: org.linkedin_partner_id || '',
  meta_pixel_id: org.meta_pixel_id || '',
  track_page_views: org.track_page_views === true,
  track_checkout_started: org.track_checkout_started === true,
  track_purchases: org.track_purchases === true,
})

const TUTORIAL_VIDEO_SOURCE =
  'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Marketing%20Attribution_1080p.mp4'

export default function MarketingTrackingWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { isAccountOwner } = AuthService

  const [trackingSettings, setTrackingSettings] = useState()
  const [initialState, setInitialState] = useState()
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  useEffect(() => {
    showLoading()
    const activeOrg = AuthService.getOrg()?.[0]

    getMyOrganizations()
      .then((res) => {
        const list = res?.data || []
        const matched = activeOrg?.uuid
          ? list.find((org) => org.uuid === activeOrg.uuid)
          : null
        const org = matched ?? activeOrg ?? list[0]
        const nextSettings = getTrackingStateFromOrg(org)

        setTrackingSettings(nextSettings)
        setInitialState(nextSettings)
        hideLoading()
      })
      .catch(() => {
        hideLoading()
      })
  }, [])

  const isDisabled =
    !trackingSettings ||
    JSON.stringify(initialState) === JSON.stringify(trackingSettings)

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setTrackingSettings((prev) => ({
      ...prev,
      [name]: checked,
    }))
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setTrackingSettings((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSave = (e) => {
    e.preventDefault()
    if (!isAccountOwner() || isDisabled) return

    setIsSaving(true)
    updateOrgDetails(trackingSettings)
      .then(() => {
        setInitialState(trackingSettings)
        setAlert({
          show: true,
          variant: 'success',
          message: 'Tracking settings have been updated.',
        })
      })
      .catch(() => {
        setAlert({
          show: true,
          variant: 'danger',
          message: 'Unable to save tracking settings. Please try again.',
        })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer />
      ) : (
        <div className="position-relative">
          <section className="wrapper">
            {alert.show && (
              <Alert
                variant={alert.variant}
                className="mb-5"
                onClose={() =>
                  setAlert({ show: false, variant: '', message: '' })
                }
                dismissible
              >
                {alert.message}
              </Alert>
            )}

            <header className="section-header">
              <div className="section-heading section-heading--secondary d-flex align-items-center justify-content-between gap-2">
                <h1 className="mb-0">Marketing Attribution</h1>
              </div>
              <p className="section-header-desc">
                Configure tracking integrations and conversion events for this
                organization.
              </p>
              <p className="section-header-desc mt-2">
                <strong>Note: </strong>
                <Button
                  variant="link"
                  className="p-0 align-baseline"
                  onClick={() => setShowTutorial(true)}
                >
                  View Tutorial
                </Button>
              </p>
            </header>

            <Modal
              show={showTutorial}
              onHide={handleCloseTutorial}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Marketing Attribution Tutorial</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <video
                  ref={tutorialVideoRef}
                  controls
                  autoPlay
                  style={{ width: '100%' }}
                >
                  <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </Modal.Body>
            </Modal>

            <Form onSubmit={handleSave}>
              <Card body className="card--sm">
                <Stack gap={4}>
                  <Form.Check
                    type="checkbox"
                    id="tracking-enabled"
                    name="tracking_enabled"
                    label="Enable tracking for this organization"
                    checked={Boolean(trackingSettings?.tracking_enabled)}
                    onChange={handleCheckboxChange}
                    disabled={!isAccountOwner()}
                  />

                  <div>
                    <h2 className="h5 mb-3">Google Tag Manager</h2>
                    <Form.Group controlId="google-tag-manager-id">
                      <Form.Label>GTM Container ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="google_tag_manager_id"
                        placeholder="GTM-XXXXXXX"
                        value={trackingSettings?.google_tag_manager_id || ''}
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                  </div>

                  <div>
                    <h2 className="h5 mb-3">Google Ads</h2>
                    <Form.Group className="mb-3" controlId="google-ads-id">
                      <Form.Label>Google Ads ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="google_ads_id"
                        placeholder="AW-XXXXXXXXX"
                        value={trackingSettings?.google_ads_id || ''}
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                    <Form.Group controlId="google-ads-conversion-label">
                      <Form.Label>Purchase Conversion Label</Form.Label>
                      <Form.Control
                        type="text"
                        name="google_ads_conversion_label"
                        placeholder="abcDEF123_xyz"
                        value={
                          trackingSettings?.google_ads_conversion_label || ''
                        }
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                  </div>

                  <div>
                    <h2 className="h5 mb-3">Meta Pixel</h2>
                    <Form.Group controlId="meta-pixel-id">
                      <Form.Label>Meta Pixel ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="meta_pixel_id"
                        placeholder="123456789012345"
                        value={trackingSettings?.meta_pixel_id || ''}
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                  </div>

                  <div>
                    <h2 className="h5 mb-3">TikTok Pixel</h2>
                    <Form.Group controlId="tiktok-pixel-id">
                      <Form.Label>TikTok Pixel ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="tiktok_pixel_id"
                        placeholder="CXXXXXXXXXXXX"
                        value={trackingSettings?.tiktok_pixel_id || ''}
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                  </div>

                  <div>
                    <h2 className="h5 mb-3">LinkedIn Insight</h2>
                    <Form.Group controlId="linkedin-partner-id">
                      <Form.Label>LinkedIn Partner ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="linkedin_partner_id"
                        placeholder="1234567"
                        value={trackingSettings?.linkedin_partner_id || ''}
                        onChange={handleInputChange}
                        disabled={!isAccountOwner()}
                      />
                    </Form.Group>
                  </div>

                  <div>
                    <h2 className="h5 mb-3">Advanced Options</h2>
                    <Stack gap={2}>
                      <Form.Check
                        type="checkbox"
                        id="track-page-views"
                        name="track_page_views"
                        label="Track page views"
                        checked={Boolean(trackingSettings?.track_page_views)}
                        onChange={handleCheckboxChange}
                        disabled={!isAccountOwner()}
                      />
                      <Form.Check
                        type="checkbox"
                        id="track-checkout-started"
                        name="track_checkout_started"
                        label="Track checkout started"
                        checked={Boolean(
                          trackingSettings?.track_checkout_started
                        )}
                        onChange={handleCheckboxChange}
                        disabled={!isAccountOwner()}
                      />
                      <Form.Check
                        type="checkbox"
                        id="track-purchases"
                        name="track_purchases"
                        label="Track purchases"
                        checked={Boolean(trackingSettings?.track_purchases)}
                        onChange={handleCheckboxChange}
                        disabled={!isAccountOwner()}
                      />
                    </Stack>
                  </div>
                </Stack>
              </Card>

              <Stack direction="horizontal" className="btn-group-flex">
                <Button
                  type="submit"
                  size="lg"
                  className="btn-width-sm"
                  disabled={!isAccountOwner() || isDisabled || isSaving}
                >
                  {isSaving ? <Spinner /> : 'Save Tracking Settings'}
                </Button>
              </Stack>
            </Form>
          </section>
        </div>
      )}
    </>
  )
}
